// Prefacturas de proveedores — Financiero → Compras y Pagos → Prefacturas de proveedores (981).
// El proveedor envía un Excel con lo atendido ANTES de facturar (fecha, sede, seguro,
// especialidad, tipo de atención, cédula y nombre del paciente, factura de copago y
// valor). Aquí se carga (el Excel se lee en el navegador) y, al procesar la factura
// recibida en Procesar Recibidos, se enlaza con ella (una prefactura ↔ una factura).
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ban, Eye, FileSpreadsheet, Link2, Loader2, Save, Upload, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BuscadorCombo } from '@/components/ui/BuscadorCombo';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useSort, SortableTh } from './tablaSort';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> => (await apiClient.post(`/rpc/${fn}`, body, FIN)).data;
const fmtF = (s: string | null) => (s ? new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString(LOCALE) : '—');
const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

interface Partner { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface Prefactura {
  id: string; proveedor: string; ruc_proveedor: string; referencia: string | null; archivo: string | null;
  fecha_desde: string | null; fecha_hasta: string | null; total: number; lineas: number; pacientes: number;
  estado: 'pendiente' | 'enlazada' | 'anulada'; factura: string | null; factura_total: number | null; creado: string; usuario: string | null;
}
interface Linea {
  fecha: string | null; sede: string | null; seguro: string | null; especialidad: string | null; servicio: string | null;
  identificacion: string | null; paciente: string | null; referencia: string | null; valor: number;
}
const ESTADO: Record<Prefactura['estado'], { t: string; cls: string }> = {
  pendiente: { t: 'Pendiente de enlazar', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  enlazada: { t: 'Enlazada', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  anulada: { t: 'Anulada', cls: 'bg-muted text-muted-foreground' },
};

// ── Lectura del Excel ───────────────────────────────────────────────────────
// Reconoce la fila de encabezados por palabras clave y rellena hacia abajo las
// celdas combinadas (un paciente con varios exámenes ocupa varias filas).
const COLS: { k: keyof Linea; re: RegExp }[] = [
  { k: 'fecha', re: /fecha/ },
  { k: 'sede', re: /sede|sucursal/ },
  { k: 'seguro', re: /seguro|aseguradora|convenio/ },
  { k: 'especialidad', re: /especialidad/ },
  { k: 'servicio', re: /tipo de atencion|atencion|servicio|descripcion|procedimiento|examen|detalle/ },
  { k: 'identificacion', re: /cedula|identificacion|pasaporte|documento/ },
  { k: 'paciente', re: /apellido|nombre|paciente/ },
  { k: 'referencia', re: /factura|copago|referencia|autorizacion/ },
  { k: 'valor', re: /valor|monto|precio|importe|total/ },
];
const RELLENAR: (keyof Linea)[] = ['fecha', 'sede', 'seguro', 'especialidad', 'identificacion', 'paciente', 'referencia'];

const aIso = (v: unknown): string | null => {
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};
const aNum = (v: unknown): number | null => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[$\s]/g, '');
  if (!s) return null;
  const n = Number(s.includes(',') && !s.includes('.') ? s.replace(',', '.') : s.replace(/,/g, ''));
  return isNaN(n) ? null : n;
};

interface Lectura { hojas: string[]; hoja: string; lineas: Linea[]; totalArchivo: number | null; avisos: string[]; columnas: string[] }

async function leerExcel(file: File, hoja?: string): Promise<Lectura> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const nombre = hoja && wb.SheetNames.includes(hoja) ? hoja : wb.SheetNames[0];
  const filas = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[nombre], { header: 1, raw: true, defval: null });
  // fila de encabezados: la primera (en las 20 primeras) con ≥ 3 columnas reconocidas
  let hIdx = -1; let mapa: Partial<Record<keyof Linea, number>> = {};
  for (let i = 0; i < Math.min(filas.length, 20) && hIdx < 0; i++) {
    const m: Partial<Record<keyof Linea, number>> = {};
    (filas[i] || []).forEach((c, j) => {
      const t = sinTildes(String(c ?? ''));
      if (!t) return;
      for (const col of COLS) if (m[col.k] === undefined && col.re.test(t)) { m[col.k] = j; break; }
    });
    if (Object.keys(m).length >= 3 && m.valor !== undefined) { hIdx = i; mapa = m; }
  }
  if (hIdx < 0) throw new Error('No se encontró la fila de encabezados (se esperan columnas como FECHA, CÉDULA, NOMBRES y VALOR).');
  const avisos: string[] = [];
  const lineas: Linea[] = [];
  let totalArchivo: number | null = null;
  const previo: Partial<Linea> = {};
  for (let i = hIdx + 1; i < filas.length; i++) {
    const f = filas[i] || [];
    const celda = (k: keyof Linea) => (mapa[k] !== undefined ? f[mapa[k]!] : null);
    const textos = f.filter((c) => c !== null && String(c).trim() !== '').map((c) => sinTildes(String(c)));
    if (!textos.length) continue;
    const valor = aNum(celda('valor'));
    if (textos.some((t) => /^total/.test(t))) { if (valor !== null) totalArchivo = valor; continue; }
    if (valor === null) continue;  // filas de notas («ENVIADO A FACTURAR», etc.)
    const l: Linea = {
      fecha: aIso(celda('fecha')), sede: celda('sede') != null ? String(celda('sede')).trim() : null,
      seguro: celda('seguro') != null ? String(celda('seguro')).trim() : null,
      especialidad: celda('especialidad') != null ? String(celda('especialidad')).trim() : null,
      servicio: celda('servicio') != null ? String(celda('servicio')).trim() : null,
      identificacion: celda('identificacion') != null ? String(celda('identificacion')).replace(/\s/g, '') : null,
      paciente: celda('paciente') != null ? String(celda('paciente')).replace(/\s+/g, ' ').trim() : null,
      referencia: celda('referencia') != null ? String(celda('referencia')).trim() : null,
      valor: Math.round(valor * 100) / 100,
    };
    for (const k of RELLENAR) {
      if (l[k] === null || l[k] === '') (l as unknown as Record<string, unknown>)[k] = previo[k] ?? null;
      else (previo as Record<string, unknown>)[k] = l[k];
    }
    lineas.push(l);
  }
  const sinPaciente = lineas.filter((l) => !l.paciente && !l.identificacion).length;
  if (sinPaciente) avisos.push(`${sinPaciente} línea(s) sin paciente identificado.`);
  if (lineas.some((l) => !l.fecha)) avisos.push('Hay líneas sin fecha.');
  const suma = Math.round(lineas.reduce((a, l) => a + l.valor, 0) * 100) / 100;
  if (totalArchivo !== null && Math.abs(totalArchivo - suma) > 0.01) avisos.push(`La suma de las líneas (${usd(suma)}) no coincide con el TOTAL del archivo (${usd(totalArchivo)}).`);
  return { hojas: wb.SheetNames, hoja: nombre, lineas, totalArchivo, avisos, columnas: Object.keys(mapa) };
}

// ── Pantalla ────────────────────────────────────────────────────────────────
export function PrefacturasProveedor() {
  const qc = useQueryClient();
  const [cargando, setCargando] = useState(false);
  const [viendo, setViendo] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [estado, setEstado] = useState('');
  const { data = [], isLoading } = useQuery<Prefactura[]>({ queryKey: ['fin-prefacturas'], queryFn: () => rpc('fin_prefacturas') });
  const filas = useMemo(() => data.filter((p) => !estado || p.estado === estado), [data, estado]);
  const { sorted, sort, toggle } = useSort(filas);
  const anular = useMutation({
    mutationFn: (id: string) => rpc('fin_prefactura_anular', { _id: id, _motivo: motivo || null }),
    onSuccess: () => { toast.success('Prefactura anulada'); setAnulando(null); setMotivo(''); void qc.invalidateQueries({ queryKey: ['fin-prefacturas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold"><FileSpreadsheet className="h-5 w-5" /> Prefacturas de proveedores</h1>
          <p className="text-sm text-muted-foreground">Cargue el Excel que el proveedor envía antes de facturar. Al procesar su factura en Procesar Recibidos, enlácela con la prefactura.</p>
        </div>
        {!cargando && <Button onClick={() => setCargando(true)}><Upload className="mr-1 h-4 w-4" /> Cargar prefactura</Button>}
      </div>

      {cargando && <CargarPrefactura onClose={() => setCargando(false)} onSaved={() => { setCargando(false); void qc.invalidateQueries({ queryKey: ['fin-prefacturas'] }); }} />}

      <Card><CardContent className="space-y-3 pt-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Estado:</span>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className="h-9 rounded-md border bg-background px-2" aria-label="Filtrar por estado">
            <option value="">Todos</option><option value="pendiente">Pendientes de enlazar</option><option value="enlazada">Enlazadas</option><option value="anulada">Anuladas</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th>
              <SortableTh col="creado" label="Cargada" sort={sort} onSort={toggle} />
              <SortableTh col="proveedor" label="Proveedor" sort={sort} onSort={toggle} />
              <SortableTh col="referencia" label="Referencia" sort={sort} onSort={toggle} />
              <SortableTh col="fecha_desde" label="Atenciones" sort={sort} onSort={toggle} />
              <SortableTh col="lineas" label="Líneas" align="right" sort={sort} onSort={toggle} />
              <SortableTh col="pacientes" label="Pacientes" align="right" sort={sort} onSort={toggle} />
              <SortableTh col="total" label="Total" align="right" sort={sort} onSort={toggle} />
              <SortableTh col="estado" label="Estado" sort={sort} onSort={toggle} />
              <th className="py-2 pr-3">Factura enlazada</th><th />
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Cargando…</td></tr>}
              {!isLoading && sorted.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">No hay prefacturas cargadas.</td></tr>}
              {sorted.map((p, i) => (
                <Fragment key={p.id}>
                  <tr className="border-b">
                    <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtF(p.creado)}<div className="text-xs text-muted-foreground">{p.usuario}</div></td>
                    <td className="py-1.5 pr-3">{p.proveedor}<div className="text-xs text-muted-foreground">{p.ruc_proveedor}</div></td>
                    <td className="py-1.5 pr-3">{p.referencia ?? '—'}<div className="text-xs text-muted-foreground">{p.archivo}</div></td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtF(p.fecha_desde)} – {fmtF(p.fecha_hasta)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.lineas}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.pacientes}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums font-medium">{usd(p.total)}</td>
                    <td className="py-1.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO[p.estado].cls}`}>{ESTADO[p.estado].t}</span></td>
                    <td className="py-1.5 pr-3 text-xs">
                      {p.factura ? <><span className="font-mono">{p.factura}</span>
                        {p.factura_total != null && Math.abs(Number(p.factura_total) - Number(p.total)) > 0.01 &&
                          <div className="text-amber-700 dark:text-amber-400">diferencia {usd(Number(p.factura_total) - Number(p.total))}</div>}</> : '—'}
                    </td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setViendo(viendo === p.id ? null : p.id)}><Eye className="mr-1 h-3.5 w-3.5" /> Ver</Button>
                      {p.estado === 'pendiente' && (anulando === p.id ? null :
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setAnulando(p.id); setMotivo(''); }}><Ban className="mr-1 h-3.5 w-3.5" /> Anular</Button>)}
                    </td>
                  </tr>
                  {anulando === p.id && (
                    <tr className="border-b bg-muted/30"><td colSpan={11} className="p-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span>Motivo de la anulación:</span>
                        <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: el proveedor envió una versión corregida"
                          className="h-8 min-w-64 flex-1 rounded border bg-background px-2" />
                        <Button size="sm" isLoading={anular.isPending} onClick={() => anular.mutate(p.id)}>Anular</Button>
                        <Button size="sm" variant="ghost" onClick={() => setAnulando(null)}>Cancelar</Button>
                      </div>
                    </td></tr>
                  )}
                  {viendo === p.id && <tr className="border-b"><td colSpan={11} className="bg-muted/20 p-2"><DetallePrefactura id={p.id} /></td></tr>}
                </Fragment>
              ))}
            </tbody>
            {sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td className="py-2 pr-3" colSpan={5}>{sorted.length} prefactura(s) · {sorted.filter((p) => p.estado === 'pendiente').length} pendiente(s) de enlazar</td>
              <td className="py-2 pr-3 text-right tabular-nums">{sorted.reduce((a, p) => a + p.lineas, 0)}</td><td />
              <td className="py-2 pr-3 text-right tabular-nums">{usd(sorted.reduce((a, p) => a + Number(p.total), 0))}</td><td colSpan={3} />
            </tr></tfoot>}
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

// Tabla de líneas (vista previa de la carga y detalle de una prefactura).
export function TablaLineasPrefactura({ lineas }: { lineas: (Linea & { registrado?: string | null })[] }) {
  return (
    <div className="max-h-[28rem] overflow-auto rounded border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background"><tr className="border-b text-left text-muted-foreground">
          <th className="py-1.5 px-2">N°</th><th className="py-1.5 pr-2">Fecha</th><th className="py-1.5 pr-2">Paciente</th><th className="py-1.5 pr-2">Identificación</th>
          <th className="py-1.5 pr-2">Especialidad</th><th className="py-1.5 pr-2">Tipo de atención</th><th className="py-1.5 pr-2">Sede</th>
          <th className="py-1.5 pr-2">Seguro</th><th className="py-1.5 pr-2">Factura copago</th><th className="py-1.5 pr-2 text-right">Valor</th>
        </tr></thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1 px-2 text-muted-foreground">{i + 1}</td>
              <td className="py-1 pr-2 whitespace-nowrap">{fmtF(l.fecha)}</td>
              <td className="py-1 pr-2">{l.paciente ?? '—'}{l.registrado !== undefined && (l.registrado
                ? <span className="ml-1 text-[10px] text-emerald-700 dark:text-emerald-400" title={`Registrado como ${l.registrado}`}>✓ registrado</span>
                : <span className="ml-1 text-[10px] text-muted-foreground">no registrado</span>)}</td>
              <td className="py-1 pr-2 font-mono">{l.identificacion ?? '—'}</td>
              <td className="py-1 pr-2">{l.especialidad ?? '—'}</td>
              <td className="py-1 pr-2">{l.servicio ?? '—'}</td>
              <td className="py-1 pr-2">{l.sede ?? '—'}</td>
              <td className="py-1 pr-2">{l.seguro ?? '—'}</td>
              <td className="py-1 pr-2">{l.referencia ?? '—'}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{usd(l.valor)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="border-t-2 font-semibold">
          <td className="py-1.5 px-2" colSpan={9}>{lineas.length} línea(s) · {new Set(lineas.map((l) => l.identificacion || l.paciente)).size} paciente(s)</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{usd(lineas.reduce((a, l) => a + Number(l.valor), 0))}</td>
        </tr></tfoot>
      </table>
    </div>
  );
}

export function DetallePrefactura({ id }: { id: string }) {
  const { data, isLoading } = useQuery<{ proveedor: string; referencia: string | null; total: number; estado: string; observaciones: string | null;
    motivo_anulacion: string | null; factura: { serie: string; fecha: string; total: number } | null; lineas: (Linea & { registrado: string | null })[] }>({
    queryKey: ['fin-prefactura', id], queryFn: () => rpc('fin_prefactura_detalle', { _id: id }),
  });
  if (isLoading || !data) return <p className="py-3 text-center text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> Cargando…</p>;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {data.proveedor}{data.referencia ? ` · ${data.referencia}` : ''}
        {data.factura && <> · <Link2 className="inline h-3 w-3" /> Factura <span className="font-mono">{data.factura.serie}</span> del {fmtF(data.factura.fecha)} por {usd(data.factura.total)}</>}
        {data.observaciones && ` · ${data.observaciones}`}{data.motivo_anulacion && ` · Anulada: ${data.motivo_anulacion}`}
      </p>
      <TablaLineasPrefactura lineas={data.lineas} />
    </div>
  );
}

function CargarPrefactura({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [proveedorId, setProveedorId] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [lectura, setLectura] = useState<Lectura | null>(null);
  const [referencia, setReferencia] = useState('');
  const [obs, setObs] = useState('');
  const [leyendo, setLeyendo] = useState(false);
  const { data: partners = [] } = useQuery<Partner[]>({ queryKey: ['fin-partners'], queryFn: () => rpc('sri_partners') });
  const proveedores = useMemo(() => partners
    .filter((p) => (p.tipo_partner || []).includes('proveedor'))
    .map((p) => ({ id: p.id, codigo: p.identificacion ?? '', nombre: p.razon_social })), [partners]);

  const leer = async (f: File, hoja?: string) => {
    setLeyendo(true);
    try {
      const r = await leerExcel(f, hoja);
      setLectura(r);
      if (!referencia || (lectura && referencia === lectura.hoja)) setReferencia(r.hoja);
      if (!r.lineas.length) toast.error('No se encontraron líneas con valor en esa hoja');
    } catch (e) { setLectura(null); toast.error((e as Error).message || 'No se pudo leer el archivo'); }
    finally { setLeyendo(false); }
  };
  const guardar = useMutation({
    mutationFn: () => rpc<{ lineas: number; total: number }>('fin_prefactura_guardar', { _data: {
      proveedor_id: proveedorId, referencia, archivo: archivo?.name ?? null, observaciones: obs || null, lineas: lectura?.lineas ?? [],
    } }),
    onSuccess: (r) => { toast.success(`Prefactura cargada: ${r.lineas} línea(s) por ${usd(r.total)}`); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const total = lectura ? lectura.lineas.reduce((a, l) => a + l.valor, 0) : 0;

  return (
    <Card className="border-2 border-primary/40"><CardContent className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <p className="font-medium">Cargar prefactura</p>
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /> Cerrar</button>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-xs font-medium text-muted-foreground">Proveedor *
          <div className="mt-1"><BuscadorCombo value={proveedorId} onChange={setProveedorId} items={proveedores} compact allowClear placeholder="Escriba el RUC o el nombre del proveedor…" /></div>
        </label>
        <label className="text-xs font-medium text-muted-foreground">Archivo Excel (.xlsx, .xls) *
          <input type="file" accept=".xlsx,.xls,.csv" className="mt-1 block w-full text-sm"
            onChange={(e) => { const f = e.target.files?.[0] ?? null; setArchivo(f); setLectura(null); if (f) void leer(f); }} />
        </label>
        {lectura && lectura.hojas.length > 1 && (
          <label className="text-xs font-medium text-muted-foreground">Hoja
            <select className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value={lectura.hoja} onChange={(e) => archivo && void leer(archivo, e.target.value)}>
              {lectura.hojas.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs font-medium text-muted-foreground">Referencia
          <input className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Ej.: MAYO - JUNIO" />
        </label>
        <label className="text-xs font-medium text-muted-foreground md:col-span-2">Observación
          <input className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value={obs} onChange={(e) => setObs(e.target.value)} />
        </label>
      </div>
      {leyendo && <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Leyendo el archivo…</p>}
      {lectura && (
        <>
          <p className="text-sm">
            <b>{lectura.lineas.length}</b> línea(s) · <b>{new Set(lectura.lineas.map((l) => l.identificacion || l.paciente)).size}</b> paciente(s) · total <b>{usd(total)}</b>
            {lectura.totalArchivo !== null && Math.abs(lectura.totalArchivo - total) <= 0.01 && <span className="ml-2 text-emerald-700 dark:text-emerald-400">✓ cuadra con el TOTAL del archivo</span>}
            <span className="ml-2 text-xs text-muted-foreground">Columnas reconocidas: {lectura.columnas.join(', ')}</span>
          </p>
          {lectura.avisos.length > 0 && (
            <ul className="list-disc rounded border border-amber-300 bg-amber-50 py-2 pl-6 pr-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              {lectura.avisos.map((a) => <li key={a}>{a}</li>)}
            </ul>
          )}
          <TablaLineasPrefactura lineas={lectura.lineas} />
        </>
      )}
      <div className="flex gap-2">
        <Button disabled={!proveedorId || !lectura?.lineas.length} isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar prefactura</Button>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
      </div>
    </CardContent></Card>
  );
}
